import { Box, Grid, Typography } from "@mui/material";
import HeaderNavBar from "./HeaderNavBar";
import "../dossier-styles.scss";
import CustomChip from "../../../UIElements/CustomChip";
import { ReactComponent as TrashIcon } from "../../../../public/icons/TrashIcon.svg";
import ReactSvgIcon from "../../../UIElements/ReactSvgIcon";
import {
    AuthUser,
    DossierDetailType,
    DossierStatusEnum,
    EstablishmentStatus,
    InstitutionalAffiliationType,
    RolesEnum,
    TabMenu,
} from "../../../../types";
import SynerpaDossierHeaderContent from "./SynerpaDossierHeaderContent";
import {
    getDossierStatusColorByUserRole,
    getValidatorDecisionInfos,
} from "../utils";
import ClientDossierHeaderContent from "./ClientDossierHeader";
import InstructorDossierHeaderContent from "./InstructorDossierHeader";
import AdminDossierHeaderContent from "./AdminDossierHeader";
import PaymentBoutton from "../../../UIElements/PaymentBoutton";
import { ValidatorDecisionInfosType } from "../OverViews/OverViewDossierWrapper";

export type headerInfosType = {
    dossierDetails: DossierDetailType | null;
    referencielName: string;
    dossierId: number | null;
    dossierStatut: string | undefined;
    establishmentName?: string;
    instructionInfos?: {
        assignedDate: string;
        instructionDeadline: string;
        processStep: number;
    };
    processingStepThreshold?: number;
    instructorList?: any[];
    assignedUser?: number | null;
    submittedAt?: string;
    instructorReview?: string;
    validatorDecision?: EstablishmentStatus;
    assignedComment?: string | null;
    assignedValidatorComment?: string | null;
    assignedValidatorUser?: AuthUser | null;
    isResendToInstruct: boolean;
};

export type DossierHeaderProps = {
    back: Function;
    setActiveTab: Function;
    activeTab: string;
    tabsMenu: TabMenu[];
    headerInfo: headerInfosType;
    deleteDossier: Function;
    canShowDeleteBtn: boolean;
    userRoleId: number;
    onInstructorAssignChange: Function;
    downloadCertificate: Function;
    downloadAllDoc: Function;
    showDownloadCertificateBtn: boolean;
    downloadContract?: Function;
    onPaymentClick?: Function;
    isPayed?: boolean;
    notShowContractBtn?: boolean;
    validatorDecisionInfos?: ValidatorDecisionInfosType;
    manageContract?: Function;
    institutionalAffiliation?: InstitutionalAffiliationType;
};

export default function DossierHeader({
    back,
    setActiveTab,
    activeTab,
    tabsMenu,
    headerInfo,
    deleteDossier,
    canShowDeleteBtn = false,
    userRoleId,
    onInstructorAssignChange,
    downloadCertificate,
    downloadAllDoc,
    showDownloadCertificateBtn = false,
    downloadContract,
    onPaymentClick,
    isPayed,
    manageContract,
    institutionalAffiliation,
}: DossierHeaderProps) {
    const { dossierDetails, establishmentName } = headerInfo;

    const isClient = userRoleId === RolesEnum.CLIENT;

    const isInstructor = userRoleId === RolesEnum.INSTRUCTEUR;
    const isSynerpaUser = userRoleId === RolesEnum.SYNERPA;

    const disablePaymentBtn = ![RolesEnum.ADMIN].includes(userRoleId);

    const dossierStatusColors = getDossierStatusColorByUserRole(
        userRoleId,
        dossierDetails
    );

    const notShowContractBtn =
        dossierDetails?.dossierStatus.code === DossierStatusEnum.FINALISE;

    const validatorDecisionInfos = dossierDetails
        ? getValidatorDecisionInfos(dossierDetails)
        : undefined;

    const disablePaymentBtnForAdmin = [
        DossierStatusEnum.FINALISE,
        DossierStatusEnum.ATTENTE_DECISION,
    ].includes(dossierDetails?.dossierStatus?.code as any);

    return (
        <Box
            sx={{
                marginLeft: "6rem",
                marginTop: "5vh",
                marginRight: "6rem",
                display: "flex",
                flexDirection: "column",

                "@media (max-width:1024px)": {
                    marginLeft: "1.5rem",
                    marginRight: "1.5rem",
                },
            }}
        >
            <HeaderNavBar back={back} />

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: "1rem",
                    marginTop: "2.3rem",
                    marginBottom: "2.3rem",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                        alignSelf: "stretch",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-end",
                            alignSelf: "stretch",
                            gap: "0.5rem",
                        }}
                    >
                        <CustomChip
                            sx={{
                                display: "flex",
                                height: "1.5rem",
                                justifyContent: "center",
                                alignItems: "center",
                                borderRadius: "1.25rem",
                                border: `1x solid ${dossierStatusColors.border}`,
                                background: `${dossierStatusColors.background}`,
                                borderColor: `${dossierStatusColors.border}`,
                                ".MuiChip-label": {
                                    color: "#023",
                                    textAlign: "center",
                                    textEdge: "cap",
                                    fontFamily: "Roboto",
                                    fontSize: "0.75rem",
                                    fontStyle: "normal",
                                    fontWeight: "500",
                                    lineHeight: "normal",
                                },
                            }}
                            label={
                                dossierStatusColors?.status ??
                                headerInfo.dossierStatut
                            }
                        />
                        <PaymentBoutton
                            onClick={onPaymentClick}
                            isPayed={isPayed}
                            disabled={
                                userRoleId === RolesEnum.ADMIN
                                    ? disablePaymentBtnForAdmin
                                    : disablePaymentBtn
                            }
                        />
                    </Box>

                    {/* Actions */}
                    {canShowDeleteBtn ? (
                        <ReactSvgIcon
                            width="1.5rem"
                            height="1.5rem"
                            ReactIcon={TrashIcon}
                            onClick={() => deleteDossier()}
                            sx={{ cursor: "pointer", float: "right" }}
                        />
                    ) : null}
                </Box>

                <Grid container spacing={1.5}>
                    <Grid
                        xs={12}
                        item
                        sm={6}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "1.5rem",
                            alignSelf: "stretch",
                            width: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                // flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "1rem",
                                alignSelf: "stretch",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: "0.5rem",
                                    alignSelf: "stretch",
                                    flex: "1 0 0",
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: "#555A5D",

                                        fontFamily: "Roboto",
                                        fontSize: "0.875rem",
                                        fontStyle: "normal",
                                        fontWeight: 400,
                                        lineHeight: "normal",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    Établissement
                                </Typography>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        color: "#555A5D",

                                        fontFamily: "Roboto",
                                        fontSize: "1rem",
                                        fontStyle: "normal",
                                        fontWeight: 500,
                                        lineHeight: "normal",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    <b>{establishmentName}</b>
                                </Typography>
                            </Box>
                            {institutionalAffiliation?.network ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: "0.5rem",
                                        alignSelf: "stretch",
                                        flex: "1 0 0",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: "#555A5D",

                                            fontFamily: "Roboto",
                                            fontSize: "0.875rem",
                                            fontStyle: "normal",
                                            fontWeight: 400,
                                            lineHeight: "normal",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        Réseau d'appartenance
                                    </Typography>
                                    <Typography
                                        variant="h1"
                                        sx={{
                                            color: "#555A5D",

                                            fontFamily: "Roboto",
                                            fontSize: "1rem",
                                            fontStyle: "normal",
                                            fontWeight: 500,
                                            lineHeight: "normal",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        <b>
                                            {institutionalAffiliation.network}
                                        </b>
                                    </Typography>
                                </Box>
                            ) : null}
                            {institutionalAffiliation?.region ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: "0.5rem",
                                        alignSelf: "stretch",
                                        flex: "1 0 0",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: "#555A5D",

                                            fontFamily: "Roboto",
                                            fontSize: "0.875rem",
                                            fontStyle: "normal",
                                            fontWeight: 400,
                                            lineHeight: "normal",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        Région d'appartenance
                                    </Typography>
                                    <Typography
                                        variant="h1"
                                        sx={{
                                            color: "#555A5D",

                                            fontFamily: "Roboto",
                                            fontSize: "1rem",
                                            fontStyle: "normal",
                                            fontWeight: 500,
                                            lineHeight: "normal",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        <b>{institutionalAffiliation.region}</b>
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>
                    </Grid>
                </Grid>

                {isClient ? (
                    <ClientDossierHeaderContent
                        back={back}
                        setActiveTab={setActiveTab}
                        activeTab={activeTab}
                        tabsMenu={tabsMenu}
                        headerInfo={headerInfo}
                        deleteDossier={deleteDossier}
                        canShowDeleteBtn={canShowDeleteBtn}
                        userRoleId={userRoleId}
                        onInstructorAssignChange={onInstructorAssignChange}
                        downloadCertificate={downloadCertificate}
                        downloadAllDoc={downloadAllDoc}
                        showDownloadCertificateBtn={showDownloadCertificateBtn}
                        downloadContract={downloadContract}
                        notShowContractBtn={notShowContractBtn}
                        validatorDecisionInfos={validatorDecisionInfos}
                        manageContract={manageContract}
                    />
                ) : null}

                {isInstructor ? (
                    <InstructorDossierHeaderContent
                        back={back}
                        setActiveTab={setActiveTab}
                        activeTab={activeTab}
                        tabsMenu={tabsMenu}
                        headerInfo={headerInfo}
                        deleteDossier={deleteDossier}
                        canShowDeleteBtn={canShowDeleteBtn}
                        userRoleId={userRoleId}
                        onInstructorAssignChange={onInstructorAssignChange}
                        downloadCertificate={downloadCertificate}
                        downloadAllDoc={downloadAllDoc}
                        showDownloadCertificateBtn={showDownloadCertificateBtn}
                        downloadContract={downloadContract}
                    />
                ) : null}

                {RolesEnum.ADMIN === userRoleId ? (
                    <AdminDossierHeaderContent
                        back={back}
                        setActiveTab={setActiveTab}
                        activeTab={activeTab}
                        tabsMenu={tabsMenu}
                        headerInfo={headerInfo}
                        deleteDossier={deleteDossier}
                        canShowDeleteBtn={canShowDeleteBtn}
                        userRoleId={userRoleId}
                        onInstructorAssignChange={onInstructorAssignChange}
                        downloadCertificate={downloadCertificate}
                        downloadAllDoc={downloadAllDoc}
                        showDownloadCertificateBtn={showDownloadCertificateBtn}
                        downloadContract={downloadContract}
                    />
                ) : null}

                {isSynerpaUser ? (
                    <SynerpaDossierHeaderContent
                        back={back}
                        setActiveTab={setActiveTab}
                        activeTab={activeTab}
                        tabsMenu={tabsMenu}
                        headerInfo={headerInfo}
                        deleteDossier={deleteDossier}
                        canShowDeleteBtn={canShowDeleteBtn}
                        userRoleId={userRoleId}
                        onInstructorAssignChange={onInstructorAssignChange}
                        downloadCertificate={downloadCertificate}
                        downloadAllDoc={downloadAllDoc}
                        showDownloadCertificateBtn={showDownloadCertificateBtn}
                    />
                ) : null}
            </Box>

            {/* </Box> */}
        </Box>
    );
}
